import { type FC, useMemo } from 'react';

import { PriceTableRowItem } from '@vakantiesnl/components/src/atoms/PriceTableRowItem';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { Discount } from '@vakantiesnl/components/src/molecules/PriceOverview/SubSections/Discount';
import { type Booking, type MicroCopy, type Search } from '@vakantiesnl/types';
import { BookingCostsType } from '@vakantiesnl/types/src/booking';
import { type Coupon } from '@vakantiesnl/types/src/booking/coupon';
import { gt, isEqual } from 'lodash';

import { useCustomStyles } from './PriceTable.style';
import { PerRoomSummary } from './SubSections/PerRoomSummary';
import { TotalPrice } from './SubSections/TotalPrice';
import { getUnitDescription } from '../../utils/accommodation/getUnitDescription';

type PriceTableProps = {
	funds?: Booking.FundsItem[];
	microCopies: MicroCopy;
	pricePerAdult: number;
	pricePerChild: number;
	pricePerBaby: number;
	totalParty: Search.Party;
	bookingCosts?: Booking.BookingCosts[];
	additionalCosts?: Booking.AdditionalCost;
	handLuggageCostsIncluded: boolean;
	hideBookingCostsIncluded?: boolean;
	partyPerRooms?: Search.PartyComposition[];
	priceTotal: number;
	isAccoOnly?: boolean;
	discounts?: Coupon[];
};

export const PriceTable: FC<PriceTableProps> = ({
	funds,
	microCopies,
	pricePerAdult,
	pricePerChild,
	pricePerBaby,
	bookingCosts,
	partyPerRooms,
	priceTotal,
	additionalCosts,
	isAccoOnly = false,
	discounts,
}) => {
	const { classes } = useCustomStyles();

	const bookingCostsPrice = useMemo(
		() => bookingCosts?.find((item) => isEqual(item.type, BookingCostsType.BOOKINGCOST))?.price,
		[bookingCosts],
	);

	/** Price > 0 of booking costs means that the booking costs are not included.
	 * Undefined means no info about it. */
	const bookingCostsPositivePrice: number | undefined = useMemo(
		() => (gt(bookingCostsPrice, 0) ? bookingCostsPrice : undefined),
		[bookingCostsPrice],
	);

	const filteredFunds = useMemo(() => funds?.filter((item) => gt(item.refCount, 0)), [funds]);

	return (
		<table className={classes.priceTable} data-cy="price-table">
			{partyPerRooms?.map((room, index) => (
				<PerRoomSummary
					key={`${index}-room`}
					roomIndex={index}
					party={room}
					pricePerAdult={pricePerAdult}
					pricePerChild={pricePerChild}
					pricePerBaby={pricePerBaby}
					microCopies={microCopies}
					isAccoOnly={isAccoOnly}
				/>
			))}

			{(bookingCostsPositivePrice || !!filteredFunds?.length || !!additionalCosts?.mandatory.payWithBooking) && (
				<>
					<tbody className={classes.tableBody} data-cy="price-table-costs-and-funds">
						<tr>
							<td>
								<Typography variant="headingXs" as="h5" className={classes.title}>
									{microCopies['price.label.mandatoryExtras']}
								</Typography>
							</td>
						</tr>

						{bookingCostsPositivePrice && (
							<PriceTableRowItem
								dataCy="booking-costs"
								title={microCopies['common.bookingcosts']}
								priceTotal={bookingCostsPositivePrice}
							/>
						)}
						{filteredFunds?.map((fund) => (
							<PriceTableRowItem
								key={fund.type}
								dataCy={`funds-item-${fund.type}`}
								title={`${fund.refCount}x ${microCopies[`common.${fund.type}`]}`}
								priceTotal={fund.priceTotal}
							/>
						))}
						{additionalCosts?.mandatory.payWithBooking?.map((additionalCost) => (
							<PriceTableRowItem
								key={additionalCost.description}
								dataCy={`additionalCosts-item-pay-with-booking-${additionalCost.id}`}
								title={`${additionalCost.refCount}x ${additionalCost.description} ${getUnitDescription(additionalCost.unitTag, microCopies)}`}
								priceTotal={additionalCost.priceTotal}
							/>
						))}
					</tbody>
					{additionalCosts?.mandatory.payAtLocation &&
						additionalCosts?.mandatory.payAtLocation.length > 0 && (
							<>
								<thead>
									<tr>
										<td>
											<Typography variant="headingXs" as="h5" className={classes.title}>
												{microCopies['price.label.payAtLocation']}
											</Typography>
										</td>
									</tr>
								</thead>
								<tbody className={classes.tableBody} data-cy="price-table-costs-at-location">
									{additionalCosts?.mandatory.payAtLocation?.map((additionalCost) => (
										<PriceTableRowItem
											key={additionalCost.description}
											dataCy={`additionalCosts-item-pay-at-location-${additionalCost.id}`}
											title={`${additionalCost.refCount}x ${additionalCost.description} ${getUnitDescription(additionalCost.unitTag, microCopies)}`}
											priceTotal={additionalCost.priceTotal}
										/>
									))}
								</tbody>
							</>
						)}
				</>
			)}

			{discounts && discounts.length > 0 && (
				<>
					<tbody className={classes.discountTableBody} data-cy="price-table-discounts">
						<tr>
							<td>
								<Typography variant="headingXs" as="h5" className={classes.title}>
									{microCopies['coupon.discount']}
								</Typography>
							</td>
						</tr>
						{discounts.map((discount) => (
							<Discount key={`${discount.discountType}${discount.couponcode}`} discount={discount} />
						))}
					</tbody>
				</>
			)}
			<tbody data-cy="price-table-total-price">
				<TotalPrice totalPrice={priceTotal} microCopies={microCopies} />
			</tbody>
		</table>
	);
};
